import getGraphQlClient from '@sleepstation/api-graphql'
import loggingApi, { LEVEL, configureLoggingApi } from '@sleepstation/logger'
import { getLocalStorageService } from '@sleepstation/services'
import { propId } from '@sleepstation/utils'
import memoize from 'fast-memoize'
import { always, defaultTo, pipe } from 'ramda'
import { Link } from 'react-router-dom'
import getErrorReporter from '../config/getErrorReporter'
import LOG_GROUP from '../const/logGroup'
import initialiseAuth from './initialiseAuth'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const logger = loggingApi.getLogger({ group: LOG_GROUP.STARTUP })

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const propIdOrNull = pipe(propId, defaultTo(null))

// -----------------------------------------------------------------------------
// Exports
// -----------------------------------------------------------------------------

const startup = memoize((environment) => {
  const { HOST, ORIGIN } = environment

  // Configure the logging API
  configureLoggingApi({
    environment: process.env.ACTIVE_ENV,
    filter: {
      includedGroups: [
        // LOG_GROUP.STARTUP,
      ],
    },
    level: LEVEL.DEBUG,
  })

  const errorReporter = getErrorReporter()
  const localStorageApi = getLocalStorageService()

  const { authApi, authContext } = initialiseAuth({
    errorReporter,
    localStorageApi,
    origin: ORIGIN.AUTH_SERVICE,
  })

  const { authorizer, onAuthFailure, onAuthSuccess } = authContext

  // Configure our main resources API
  const graphQLClient = getGraphQlClient({
    origin: ORIGIN.APP,
    errorReporter,
    authorizer,
    onAuthFailure,
    onAuthSuccess,
    cacheExchangeConfig: {
      // The keys object allows us to either define how URQL should derive a
      // unique id for any types that do not have an id field, or to explicitly
      // return null to tell URQL that the type is an embedded type, and should
      // be treated as part of its parent.
      keys: {
        ProjectedSleepEfficiency: always(null),
        EnrolmentProjection: always(null),
        SleepDiaryGap: always(null),
        Boundary: always(null),
        FragmentedSection: always(null),
        Consideration: always(null),
        Scale: always(null),
        ReportResult: always(null),
        Currency: always(null),
        ProductPrice: always(null),
        ResourceImage: always(null),
        Routing: always(null),
        ResourceCategory: always(null),
        // Depending on the context, SleepReport can be a transient object that
        // doesn't have an ID, in which case we treat it as an embedded object.
        SleepReport: propIdOrNull,
      },
    },
  })

  const linkContext = {
    // Component used for internal links
    LinkComponent: Link,
    // Map of tokens within URLs that will be automatically replaced at
    // point-of-use
    tokenMap: {
      railsApp: HOST.APP,
    },
  }

  logger.info(`Startup complete`)

  return {
    errorReporter,
    localStorageApi,
    graphQLClient,
    linkContext,
    authApi,
    authContext,
  }
})

export default startup
